import type { MagicJobStatus } from '@/types/gql.generated';
import { createContext } from '@/utils';
import type { MutateArg } from '../../hooks/useRunMagicJob/types';
import type { DecoratedMagicJob, PromptSource } from '../../types';

export type CreateJobArg = MutateArg & {
  promptSource: PromptSource;
  model?: string;
  systemPromptId?: string;
};

type Callbacks = {
  onSuccess?: (job: DecoratedMagicJob) => void;
  onError?: () => void;
  onSettled?: () => void;
};

export type ChatJobActivity = {
  // A create-* tool is in flight — "a magic job is running"
  isActive: boolean;
  // The chat is awaiting/streaming any response, create or not
  isResponding: boolean;
  statusIcon: string | null;
  statusMessage: string | null;
};

// The whole of what a v2 chat stream reports about the server-side MagicJob its
// create-* tool is running. `updatedAt` is client-stamped: the stream is the
// only clock the job has until a fetch carries its row.
export type StreamedChatJob = {
  id: string;
  status: MagicJobStatus;
  statusIcon: string | null;
  statusMessage: string | null;
  updatedAt: Date;
};

export type JobContextType = {
  history: DecoratedMagicJob[];
  job: DecoratedMagicJob | null;
  createJob: (arg: CreateJobArg, options?: Callbacks) => void;
  createJobError: Error | null;
  isCreating: boolean;
  reset: () => void;
  setHistory: (jobs: DecoratedMagicJob[]) => void;
  // Merge jobs into `history` by id, leaving jobs the caller doesn't know about
  // in place — the chat's fetched job list and the stream's own job payload are
  // both partial views, and each learns about a job before the other does.
  // A merge that changes nothing leaves `history` untouched by reference, so
  // callers can merge unconditionally without looping an effect.
  mergeHistoryJobs: (jobs: DecoratedMagicJob[]) => void;
  // Apply what the v2 stream knows about a job on top of whatever `history`
  // already holds for it. Narrower than `mergeHistoryJobs` on purpose: the
  // stream reports identity and progress only, so everything a job carries that
  // just the server can supply — its events, prompt, file, real createdAt —
  // has to survive a stream update rather than be blanked by it.
  recordStreamedJob: (job: StreamedChatJob) => void;
  timeZone?: string;
  // Experience-derived magic-job options, when the chat is operating within an
  // experience (otherwise undefined). Consumed by the v2 chat request and the
  // v1 createJob flow alike.
  titlePrefix?: string;
  descriptionFooter?: string;
  chatJobActivity: ChatJobActivity;
  setChatJobActivity: (value: ChatJobActivity) => void;
  // Latches true on the first `chatJobActivity.isActive` edge and stays true
  // until reset — a "this session started a chat job" signal that outlives
  // the AI-SDK stream (which dies when the tab is backgrounded).
  hasStartedChatJob: boolean;
  // Latches true on the first streamed turn of any kind, until reset. Only
  // /magic reports it, and only behind `chatModelHandlesFirstMessage`, where
  // the first message can get a plain reply instead of a create job and the
  // page still has to leave its hero layout.
  hasStartedChatTurn: boolean;
  markChatTurnStarted: () => void;
  // The latest job in `history` is still running server-side. `history` is the
  // durable record of the chat's jobs — v2 seeds it from the stream's own job
  // payload and syncs it from the fetched chat — so unlike `chatJobActivity`,
  // which is derived from the live stream, this survives both the stream dying
  // on a tab switch and react-query refetching the chat on window refocus.
  // Ungated on purpose: callers that must distinguish "this session started
  // this job" from "we mounted a chat that already had one" apply their own
  // `hasStartedChatJob` gate (see `computeAppState`).
  hasPendingChatJob: boolean;
  // A magic job is running: the live stream says so, or the job history does.
  // Prefer this over `chatJobActivity.isActive` for anything that means "a job
  // is in progress" rather than "the stream is connected" — the two only differ
  // while the stream is dead, which is exactly when the difference matters.
  isChatJobRunning: boolean;
};

export const JobContext = createContext<JobContextType>(
  {} as JobContextType,
  'JobContext'
);
